import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Star,
  Truck,
  RefreshCcw,
  Share2,
  Minus,
  Plus,
  Check,
} from "lucide-react";
import Layout from "../components/layout";
import axios from "axios";
import { API } from "../utils/api";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LoadingModal } from "../components/loading";
import { trackEvent } from "../metapixel";
import { toast } from "react-hot-toast";

const Container = ({ children, className = "" }) => (
  <div className={`mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-2xl border border-black/10 bg-white shadow-sm ${className}`}
  >
    {children}
  </div>
);

const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/60 px-3 py-1 text-xs font-bold text-black/65 backdrop-blur">
    {children}
  </span>
);

const PrimaryButton = ({ children, className = "", ...props }) => (
  <button
    className={`inline-flex items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 active:scale-[0.98] ${className}`}
    {...props}
  >
    {children}
  </button>
);

const GhostButton = ({ children, className = "", ...props }) => (
  <button
    className={`inline-flex items-center justify-center gap-2 rounded-xl border border-black/10 bg-white px-4 py-2 text-sm font-semibold text-black/80 shadow-sm transition hover:bg-black/5 active:scale-[0.98] ${className}`}
    {...props}
  >
    {children}
  </button>
);

const IconButton = ({ children, className = "", ...props }) => (
  <button
    className={`inline-flex items-center justify-center rounded-xl border border-black/10 bg-white p-2 text-black/70 shadow-sm transition hover:bg-black/5 active:scale-[0.98] ${className}`}
    {...props}
  >
    {children}
  </button>
);

const money = (n) =>
  n?.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function Stars({ value = 4.7 }) {
  const full = Math.floor(value);
  const hasHalf = value - full >= 0.5;
  const total = 5;

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center">
        {Array.from({ length: total }).map((_, i) => {
          const filled = i < full;
          const half = !filled && hasHalf && i === full;
          return (
            <span key={i} className="relative inline-flex">
              <Star
                className={`h-4 w-4 ${
                  filled ? "text-orange-600" : "text-black/20"
                }`}
                fill={filled ? "currentColor" : "none"}
              />
              {half && (
                <span
                  className="absolute left-0 top-0 overflow-hidden"
                  style={{ width: "50%" }}
                >
                  <Star
                    className="h-4 w-4 text-orange-600"
                    fill="currentColor"
                  />
                </span>
              )}
            </span>
          );
        })}
      </div>
      <span className="text-xs font-bold text-black/60">
        {value.toFixed(1)} (128 reviews)
      </span>
    </div>
  );
}

function Accordion({ items }) {
  const [open, setOpen] = useState(items?.[0]?.key ?? null);

  return (
    <div className="space-y-2">
      {items.map((it) => (
        <div
          key={it.key}
          className="rounded-2xl border border-black/10 bg-white"
        >
          <button
            className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
            onClick={() => setOpen((cur) => (cur === it.key ? null : it.key))}
          >
            <div className="text-sm font-extrabold text-black/80">
              {it.title}
            </div>
            <div className="text-xs font-black text-black/50">
              {open === it.key ? "—" : "+"}
            </div>
          </button>
          {open === it.key && (
            <div className="px-4 pb-4 text-sm font-medium leading-relaxed text-black/60">
              {it.content}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const RelatedCard = ({ p }) => {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(`/product/${p.slug}`)}
      className="group cursor-pointer overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm transition hover:shadow-md"
    >
      <div className="aspect-[4/3] overflow-hidden bg-black/5">
        <img
          src={p.img}
          alt={p.title}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]"
          loading="lazy"
        />
      </div>
      <div className="p-4 space-y-2">
        <div className="text-[11px] font-bold uppercase tracking-wide text-black/45">
          {p.category}
        </div>
        <div className="line-clamp-1 text-sm font-extrabold text-black/80">
          {p.title}
        </div>
        <div className="text-sm font-extrabold text-orange-600">
          ৳ {money(p.discountPrice || p.basePrice)}
        </div>
      </div>
    </div>
  );
};

export default function ProductDetailsPage() {
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState({});

  const { slug } = useParams();

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/product/get/${slug}`)
      .then((res) => {
        setProduct(res.data);
        setActiveImg(res.data.img);
        setVariant(res.data?.variants?.[0]);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching product data:", err);
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  trackEvent("ViewContent", {
    content_ids: [product._id], // or SKU
    content_name: product.title,
    content_type: "product",
    value: product.basePrice,
    currency: "BDT",
  });

  const [activeImg, setActiveImg] = useState(product?.img);
  const [variant, setVariant] = useState(product?.variants?.[0]);
  const [qty, setQty] = useState(1);
  const [stock, setStock] = useState(0);

  useEffect(() => {
    if (variant) {
      setStock(variant.stock);
    }
  }, [variant]);

  const inc = () => setQty((q) => Math.min(99, q + 1));
  const dec = () => setQty((q) => Math.max(1, q - 1));

  const handleAddToCart = () => {
    let cart = [];

    try {
      const storedCart = JSON.parse(localStorage.getItem("cart"));
      if (Array.isArray(storedCart)) {
        cart = storedCart;
      }
    } catch (err) {
      cart = [];
    }

    const item = {
      pid: product.pid,
      sku: variant.sku,
      variant: variant.name,
      title: product.title,
      img: variant.img,
      quantity: qty,
      price: variant ? variant.price : product.basePrice,
    };

    const existingIndex = cart.findIndex(
      (cartItem) => cartItem.sku === item.sku
    );

    if (existingIndex >= 0) {
      cart[existingIndex].quantity += item.quantity;
    } else {
      cart.push(item);
    }

    localStorage.setItem("cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cartUpdated"));
    toast.success("Added to cart!");
    trackEvent("AddToCart", {
      content_ids: [variant.sku],
      content_name: product.title,
      content_type: "product",
      value: variant.price,
      currency: "BDT",
    });
  };

  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const url = `https://shop.retrorevivebd.com/p/${product.slug}`;

    try {
      await navigator.share({
        title: product?.title || "Retro Revive BD",
        text: (product?.description || "").slice(0, 120),
        url: url,
      });
      setCopied(true);

      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  const [related, setRelated] = useState([]);
  useEffect(() => {
    if (product?.category) {
      axios
        .get(`${API}/products/get-related/${product.category}`)
        .then((res) => {
          const relatedProducts = res.data.filter(
            (p) => p.slug !== product.slug
          );
          setRelated(relatedProducts);
        })
        .catch((err) => {
          console.error("Error fetching related products:", err);
        });
    }
  }, [product]);

  if (!product) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-sm font-medium text-black/60">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-[#fbf7f1] text-black/90">
        {loading && <LoadingModal text="Loading products..." />}
        {/* Top bar */}
        <header className="border-b border-black/10 bg-[#fbf7f1]/80 backdrop-blur">
          <Container>
            <div className="flex flex-col gap-3 py-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <Link
                  to="/products"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-black/60 hover:text-black"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to shop
                </Link>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Pill>{product?.category}</Pill>
                  <span className="text-xs font-bold text-black/40">
                    SKU: {product?.sku}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* <IconButton aria-label="Add to wishlist">
                  <Heart className="h-5 w-5" />
                </IconButton> */}
                <IconButton aria-label="Share product" onClick={handleShare}>
                  <Share2 className="h-5 w-5" />
                </IconButton>

                {copied && (
                  <div className="absolute right-0 mt-2 rounded-lg bg-black px-2 py-1 text-xs text-white">
                    Link copied!
                  </div>
                )}
              </div>
            </div>
          </Container>
        </header>

        <main className="py-8 sm:py-10">
          <Container>
            {/* Main grid */}
            <div className="grid gap-6 lg:grid-cols-2 lg:gap-10">
              {/* Gallery */}
              <div className="space-y-4">
                <Card className="overflow-hidden">
                  <div className="aspect-square bg-black/5">
                    {activeImg && (
                      <img
                        src={activeImg}
                        alt={product?.title}
                        className="h-full w-full object-cover"
                      />
                    )}
                  </div>
                </Card>

                {/* Thumbnails */}
                <div className="grid grid-cols-6 gap-3">
                  {product?.gallery?.map((img) => (
                    <button
                      key={img}
                      onClick={() => setActiveImg(img)}
                      className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition ${
                        activeImg === img
                          ? "border-orange-600"
                          : "border-black/10 hover:bg-black/5"
                      }`}
                      aria-label="Select product image"
                    >
                      <div className="aspect-square bg-black/5">
                        <img
                          src={img}
                          alt="thumb"
                          className="h-full w-full object-cover"
                        />
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Details */}
              <div className="space-y-5">
                <div>
                  <h1 className="text-2xl font-black tracking-tight text-black/85 sm:text-3xl">
                    {product?.title}
                  </h1>
                  {/* <div className="mt-3">
                    <Stars value={product?.rating} />
                  </div> */}
                </div>

                {/* Price */}
                <Card className="p-4 sm:p-5">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <div className="text-sm font-bold text-black/50">
                        Price
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        {product?.variants?.length > 0 ? (
                          <span className="text-2xl font-black text-orange-600">
                            ৳{" "}
                            {money(
                              variant ? variant.price : product?.basePrice
                            )}
                          </span>
                        ) : (
                          <span className="text-2xl font-black text-orange-600">
                            ৳
                            {money(
                              product?.discountPrice
                                ? product?.discountPrice
                                : product?.basePrice
                            )}
                          </span>
                        )}

                        {product?.discountPrice && (
                          <>
                            <span className="text-sm font-bold text-black/35 line-through">
                              ৳{money(product?.basePrice)}
                            </span>
                            <span className="rounded-full bg-emerald-600/10 px-2 py-1 text-xs font-black text-emerald-700">
                              Save{" "}
                              {Math.round(
                                ((product?.basePrice -
                                  (product?.discountPrice ??
                                    product?.basePrice)) /
                                  product?.basePrice) *
                                  100
                              )}
                              %
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="text-sm font-bold text-black/60">
                      {stock > 0 ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="grid h-6 w-6 place-items-center rounded-full bg-emerald-600/10">
                            <Check className="h-4 w-4 text-emerald-700" />
                          </span>
                          In stock ({stock})
                        </span>
                      ) : (
                        <span className="text-red-600">Out of stock</span>
                      )}
                    </div>
                  </div>
                </Card>

                {/* Variant */}
                <div className="space-y-2">
                  <div className="text-sm font-extrabold text-black/75">
                    Variant: {variant?.name}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {product?.variants?.map((v) => (
                      <button
                        key={v?.sku}
                        onClick={() => {
                          setVariant(v);
                          setActiveImg(v?.img || product?.img);
                        }}
                        className={`rounded-xl border  text-sm font-bold shadow-sm transition ${
                          variant?.sku === v?.sku
                            ? "border-orange-600 bg-orange-600/10 text-orange-700"
                            : "border-black/10 bg-white text-black/70 hover:bg-black/5"
                        }`}
                      >
                        <img
                          src={v?.img}
                          alt={v?.name}
                          className="h-16 w-16 rounded-xl object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Qty + CTAs */}
                <Card className="p-4 sm:p-5">
                  <div className="flex flex-col lg:flex-row w-full sm:flex-col gap-4 justify-between sm:items-center sm:justify-between">
                    {/* qty */}
                    <div className="flex lg:flex-row items-center justify-between gap-4 sm:w-full">
                      <div className="text-sm font-extrabold text-black/70">
                        Quantity
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={dec}
                          className="grid h-10 w-10 place-items-center rounded-xl border border-black/10 bg-white text-black/70 shadow-sm transition hover:bg-black/5 active:scale-[0.98]"
                        >
                          <Minus className="h-4 w-4" />
                        </button>

                        <div className="min-w-[44px] text-center text-sm font-black text-black/80">
                          {qty}
                        </div>

                        <button
                          onClick={inc}
                          disabled={qty >= stock}
                          className="grid h-10 w-10 place-items-center rounded-xl border border-black/10 bg-white text-black/70 shadow-sm transition hover:bg-black/5 active:scale-[0.98]"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* CTAs */}
                    <div className="flex lg:flex-row lg:w-1/2 sm:w-full sm:flex-col gap-2">
                      <PrimaryButton
                        className="h-11 w-full disabled:opacity-50"
                        disabled={stock === 0}
                        onClick={handleAddToCart}
                      >
                        Add to Cart
                      </PrimaryButton>
                      {/* <GhostButton className="h-11 w-full">Buy Now</GhostButton> */}
                    </div>
                  </div>

                  <div className="mt-4 text-xs font-medium text-black/55">
                    By placing an order, you agree to our store policies.
                  </div>
                </Card>

                {/* Trust badges */}
                <div className="grid gap-3 sm:grid-cols-2">
                  {/* s */}
                  <TrustBadge
                    Icon={Truck}
                    title="Fast Shipping"
                    desc="Tracked delivery"
                  />
                  <TrustBadge
                    Icon={RefreshCcw}
                    title="Easy Returns"
                    desc="3-day returns"
                  />
                </div>
              </div>
            </div>

            {/* Description + Accordion */}
            <div className="mt-10 grid gap-6 lg:grid-cols-2 lg:gap-10">
              <Card className="p-4 sm:p-6">
                <div className="text-sm font-extrabold text-black/80">
                  Highlights
                </div>
                <ul className="mt-4 space-y-3">
                  {product?.highlights?.map((h) => (
                    <li
                      key={h}
                      className="flex items-start gap-3 text-sm font-medium text-black/60"
                    >
                      <span className="mt-0.5 inline-block h-2.5 w-2.5 rounded-full bg-orange-600/70" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-7 text-sm font-extrabold text-black/80">
                  Description
                </div>
                <p className="mt-3 text-sm font-medium leading-relaxed text-black/60">
                  {product?.description}
                </p>
              </Card>

              <Accordion
                items={[
                  {
                    key: "shipping",
                    title: "Shipping Information",
                    content:
                      "Orders are processed within 24–48 hours. Standard delivery typically takes 1–3 business days depending on location. You’ll receive tracking as soon as your order ships.",
                  },
                  {
                    key: "returns",
                    title: "Returns & Exchanges",
                    content:
                      "We offer 3-day returns on unused items in original packaging. If your item arrives damaged, contact support within 48 hours with photos and we’ll make it right.",
                  },
                  // {
                  //   key: "care",
                  //   title: "Care Instructions",
                  //   content:
                  //     "For fabric products, spot clean or gentle wash where applicable. Avoid high heat drying. Always check the product label for best results.",
                  // },
                ]}
              />
            </div>

            {/* Related products */}
            <section className="mt-12 sm:mt-16">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <h2 className="text-xl font-black tracking-tight text-black/85">
                    Related Products
                  </h2>
                  <p className="mt-1 text-sm font-medium text-black/55">
                    You might also like these nostalgic picks.
                  </p>
                </div>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {related.map((p) => (
                  <RelatedCard key={p.id} p={p} />
                ))}
              </div>
            </section>
          </Container>
        </main>
      </div>
    </Layout>
  );
}

function TrustBadge({ Icon, title, desc }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-2xl bg-black/5">
          <Icon className="h-5 w-5 text-black/70" />
        </span>
        <div>
          <div className="text-sm font-extrabold text-black/80">{title}</div>
          <div className="text-xs font-medium text-black/55">{desc}</div>
        </div>
      </div>
    </Card>
  );
}
